import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, List, ListItem,
    CircularProgress, Alert, Link, Stack, Paper, Divider,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    Accordion, AccordionSummary, AccordionDetails, Button, Chip,
    Tabs, Tab
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Gavel as GavelIcon,
    Schedule as ScheduleIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import LinksTable, { LinkItem } from '../LinksTable';
import { fetchTournamentData, getCachedTournamentData } from '../../utils/tournamentScraper';
import { formatLocation, getLiveBarnLink } from '../../utils/eventUtils';
import { getRink, RINK_DATA, Rink } from '../../utils/locationUtils';
import { MhrRating, TournamentGame, TournamentPool } from '../../types';
import { MHR_RATINGS_2026_PW_MAJOR, getMhrRating } from '../../mhr-ratings';
import { logEvent } from '../../analytics';
import { useRoster } from '../../RosterContext';
import AdvancementPredictionsCard from './AdvancementPredictionsCard';

const ccmWorldInviteLinks2026: LinkItem[] = [
    { label: 'Website', url: 'https://200x85.com/ccm-world-invite/', displayText: 'CCM World Invite' },
    { label: 'Jets Schedule', url: 'https://my.200x85.com/tournaments/1a0a158e-5bf4-4e1e-93a9-01bb7e7963ba/teams/94e4431b-9173-4d1a-a686-7a4fb8ee13cb', displayText: 'Jets Schedule (my.200x85.com)' },
    { label: 'Overall Standings', url: 'https://my.200x85.com/tournaments/1a0a158e-5bf4-4e1e-93a9-01bb7e7963ba/standings?division=994', displayText: 'Division Standings (12U: 2014 Jetspeed)' },
    { label: 'Tournament Rules', url: 'https://200x85.com/ccm-world-invite/?event=Rules', displayText: 'Official Rules & Tiebreakers' },
    { label: 'Facilities', url: 'https://200x85.com/ccm-world-invite/?event=Facilities', displayText: 'Rink Locations & Maps' },
];

/**
 * Formats game date & time string to explicitly indicate Central Time (CDT).
 */
export const formatCentralGameTime = (dateTimeStr: string): string => {
    if (!dateTimeStr) return '';
    const clean = dateTimeStr.replace(/\s+/g, ' ').trim();
    if (clean.includes('CDT') || clean.includes('CST') || clean.includes('CT')) {
        return clean;
    }
    return `${clean} CDT`;
};

const TournamentGameDisplay: React.FC<{ game: TournamentGame; ratings: Record<string, MhrRating> }> = ({ game, ratings }) => {
    const opponentTeam = game.homeTeam.includes('Jets') ? game.visitorTeam : game.homeTeam;
    const rink = getRink(game.location);
    const rinkData = rink !== Rink.Unknown ? RINK_DATA.get(rink) : null;

    const renderTeamWithRating = (teamName: string) => {
        const ratingInfo = getMhrRating(teamName, ratings);
        const isJets = teamName.includes('Jets');
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: isJets ? 700 : 500,
                        color: isJets ? 'primary.main' : 'text.primary',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                >
                    {teamName}
                </Typography>
                {ratingInfo && (
                    <Link
                        href={ratingInfo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => logEvent('click_mhr_link', { team_name: teamName })}
                        sx={{ textDecoration: 'none', ml: 1, flexShrink: 0 }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            ({ratingInfo.rating.toFixed(1)})
                        </Typography>
                    </Link>
                )}
            </Box>
        );
    };

    return (
        <ListItem sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2.5 } }}>
            <Box sx={{ width: '100%' }}>
                {/* Header Bar: DateTime on Left, Status Badge / Score on Right */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <ScheduleIcon sx={{ fontSize: '1rem', color: 'primary.main', flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                            {formatCentralGameTime(game.dateTime)}
                        </Typography>
                    </Stack>
                    <Box sx={{ flexShrink: 0 }}>
                        {game.homeScore !== '-' || game.visitorScore !== '-' ? (
                            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                                {game.homeScore} - {game.visitorScore}
                            </Typography>
                        ) : (
                            <Chip
                                label={game.status || 'SCHEDULED'}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ height: 22, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700 }}
                            />
                        )}
                    </Box>
                </Stack>

                {/* Team Matchup Box (Full Horizontal Width) */}
                <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.25 }, mb: 1, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                    {/* HOME Team */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <Chip
                            label="HOME"
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {renderTeamWithRating(game.homeTeam)}
                        </Box>
                    </Stack>
                    {/* AWAY Team */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            label="AWAY"
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {renderTeamWithRating(game.visitorTeam)}
                        </Box>
                    </Stack>
                </Paper>

                {/* Rink Details & Action Links */}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={0.5} sx={{ pl: 0.5 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
                            {formatLocation(game.location)}{rinkData?.address ? ` • ${rinkData.address}` : ''}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: { xs: 0.5, sm: 0 } }}>
                        {game.boxscoreUrl && (
                            <Link
                                href={game.boxscoreUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                fontWeight="600"
                                onClick={() => logEvent('click_tournament_boxscore', { game_id: game.id, opponent: opponentTeam })}
                            >
                                Box Score
                            </Link>
                        )}
                        {getLiveBarnLink(game.location, game.dateTime)}
                    </Stack>
                </Stack>
            </Box>
        </ListItem>
    );
};

const TournamentStandingsTable: React.FC<{ pool: TournamentPool; ratings: Record<string, MhrRating>; isJetsPool?: boolean }> = ({ pool, ratings, isJetsPool }) => {
    return (
        <Paper
            variant="outlined"
            elevation={0}
            sx={{
                mb: { xs: 2.5, sm: 3 },
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: isJetsPool ? 'primary.main' : 'divider',
                borderWidth: isJetsPool ? 2 : 1
            }}
        >
            <Box
                sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: isJetsPool ? 'primary.50' : 'grey.100',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { sm: 'center' },
                    gap: 0.75
                }}
            >
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' }, fontWeight: 700 }}>
                            Pool {pool.poolName.replace(/^Pool\s*/i, '')}
                        </Typography>
                        {isJetsPool && (
                            <Chip label="Jets Pool" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                        )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        Top seed advances to Championship Bracket
                    </Typography>
                </Box>
                <Chip label="12U: 2014 Jetspeed" size="small" variant="outlined" sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }} />
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Team</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>GP</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>W</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>T</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>L</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>PTS</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>GF</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>GA</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>GD</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>QUOT</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>SEED</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pool.standings.map((team) => {
                            const isJets = team.teamName.includes('Jets');
                            const ratingInfo = getMhrRating(team.teamName, ratings);
                            return (
                                <TableRow
                                    key={team.teamName}
                                    sx={{
                                        bgcolor: isJets ? 'primary.50' : 'inherit',
                                        '&:hover': { bgcolor: isJets ? 'primary.100' : 'action.hover' }
                                    }}
                                >
                                    <TableCell component="th" scope="row">
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {team.teamUrl ? (
                                                <Link
                                                    href={team.teamUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => logEvent('click_tournament_team_link', { team_name: team.teamName })}
                                                    color="inherit"
                                                    underline="hover"
                                                    sx={{ fontWeight: isJets ? 700 : 400 }}
                                                >
                                                    <Typography variant="body2">{team.teamName}</Typography>
                                                </Link>
                                            ) : (
                                                <Typography variant="body2" sx={{ fontWeight: isJets ? 700 : 400 }}>{team.teamName}</Typography>
                                            )}
                                            {ratingInfo && (
                                                <Link
                                                    href={ratingInfo.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => logEvent('click_mhr_link', { team_name: team.teamName })}
                                                    sx={{ textDecoration: 'none', ml: 1 }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        ({ratingInfo.rating.toFixed(1)})
                                                    </Typography>
                                                </Link>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">{team.gp}</TableCell>
                                    <TableCell align="right">{team.w}</TableCell>
                                    <TableCell align="right">{team.t}</TableCell>
                                    <TableCell align="right">{team.l}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{team.pts}</TableCell>
                                    <TableCell align="right">{team.gf}</TableCell>
                                    <TableCell align="right">{team.ga}</TableCell>
                                    <TableCell align="right">{team.gd}</TableCell>
                                    <TableCell align="right">{team.quot}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{team.seed}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

const formatTimeAgo = (isoString?: string | null): string => {
    if (!isoString) return '';
    try {
        const date = new Date(isoString);
        const now = new Date();
        const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
        if (diffSec < 45) return 'just now';
        if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
        if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
        return '';
    }
};

const CcmWorldInvite2026Page: React.FC = () => {
    const { isAuthReady } = useRoster();
    const cached = useMemo(() => getCachedTournamentData('ccm-world-invite-2026'), []);
    const [games, setGames] = useState<TournamentGame[]>(cached?.games || []);
    const [standings, setStandings] = useState<TournamentPool[]>(cached?.pools || []);
    const [lastUpdated, setLastUpdated] = useState<string | null>(cached?.updatedAt || null);
    const [isStale, setIsStale] = useState<boolean>(cached?.stale || false);
    const [loading, setLoading] = useState(!cached || (cached.games.length === 0 && cached.pools.length === 0));
    const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedPoolTab, setSelectedPoolTab] = useState<string>('E');

    const loadData = useCallback(async (force = false) => {
        if (!games.length && !standings.length) {
            setLoading(true);
        } else {
            setIsCheckingUpdates(true);
        }
        setError(null);

        try {
            const data = await fetchTournamentData('ccm-world-invite-2026', { force });
            setGames(data.games || []);
            setStandings(data.pools || []);
            setLastUpdated(data.updatedAt || new Date().toISOString());
            setIsStale(data.stale || false);
        } catch (e: any) {
            console.error('Error fetching tournament data:', e);
            if (!games.length && !standings.length) {
                setError(e.message || 'Could not retrieve tournament schedule and standings.');
            }
        } finally {
            setLoading(false);
            setIsCheckingUpdates(false);
        }
    }, [games.length, standings.length]);

    useEffect(() => {
        if (!isAuthReady) return;
        loadData(false);
    }, [isAuthReady, loadData]);

    // Jets pool (Pool E)
    const jetsPool = useMemo(() => {
        return standings.find(p => p.standings.some(s => s.teamName.toLowerCase().includes('jets'))) || standings.find(p => p.poolName === 'E') || standings[0];
    }, [standings]);

    const activePool = useMemo(() => {
        if (selectedPoolTab === 'ALL') return null;
        return standings.find(p => p.poolName === selectedPoolTab) || jetsPool;
    }, [selectedPoolTab, standings, jetsPool]);

    return (
        <>
            {/* Live Updates & Status Toolbar */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, px: 0.5 }}>
                <Box>
                    {isCheckingUpdates ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CircularProgress size={12} /> Checking for score updates...
                        </Typography>
                    ) : lastUpdated ? (
                        <Typography variant="caption" color="text.secondary">
                            Schedule & Standings Live • Last checked {formatTimeAgo(lastUpdated)}
                        </Typography>
                    ) : null}
                </Box>
                <Button
                    size="small"
                    startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
                    onClick={() => loadData(true)}
                    disabled={isCheckingUpdates || loading}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1 }}
                >
                    Refresh
                </Button>
            </Stack>

            {isStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Showing cached tournament data. We'll automatically update when the upstream connection refreshes.
                </Alert>
            )}

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {!loading && !error && (
                <>
                    {/* Schedule & Results */}
                    <Paper variant="outlined" elevation={0} sx={{ mb: { xs: 2.5, sm: 4 }, borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.100' }}>
                            <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                                Jets Schedule & Results
                            </Typography>
                        </Box>
                        {games.length > 0 ? (
                            <List disablePadding>
                                {games.map((game, index) => (
                                    <React.Fragment key={game.id}>
                                        {index > 0 && <Divider component="li" />}
                                        <TournamentGameDisplay game={game} ratings={MHR_RATINGS_2026_PW_MAJOR} />
                                    </React.Fragment>
                                ))}
                            </List>
                        ) : (
                            <Typography sx={{ p: 2 }}>Schedule not yet available.</Typography>
                        )}
                    </Paper>

                    {/* Live Advancement Predictions & Playoff Outlook Card */}
                    <AdvancementPredictionsCard games={games} standings={standings} tournamentKey="ccm-world-invite-2026" />

                    {/* Standings Section */}
                    {standings.length > 0 && (
                        <Box sx={{ mb: { xs: 2.5, sm: 4 } }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
                                <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' }, fontWeight: 700 }}>
                                    Division Standings
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    32 Teams across 8 Pools (Pools A – H)
                                </Typography>
                            </Stack>

                            {/* Pool Tabs */}
                            <Paper variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>
                                <Tabs
                                    value={selectedPoolTab}
                                    onChange={(_, newVal) => setSelectedPoolTab(newVal)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    aria-label="Pool Standings Tabs"
                                    sx={{ minHeight: 40 }}
                                >
                                    <Tab label="Pool E (Jets)" value="E" sx={{ minHeight: 40, py: 1, fontWeight: 'bold' }} />
                                    {standings
                                        .filter(p => p.poolName !== 'E')
                                        .map(p => (
                                            <Tab key={p.poolName} label={`Pool ${p.poolName}`} value={p.poolName} sx={{ minHeight: 40, py: 1 }} />
                                        ))}
                                    <Tab label="View All Pools" value="ALL" sx={{ minHeight: 40, py: 1, fontWeight: 'bold' }} />
                                </Tabs>
                            </Paper>

                            {/* Render Active Pool Table or All Pools */}
                            {selectedPoolTab === 'ALL' ? (
                                standings.map(pool => (
                                    <TournamentStandingsTable
                                        key={pool.poolName}
                                        pool={pool}
                                        ratings={MHR_RATINGS_2026_PW_MAJOR}
                                        isJetsPool={pool.poolName === 'E' || pool.standings.some(s => s.teamName.toLowerCase().includes('jets'))}
                                    />
                                ))
                            ) : activePool ? (
                                <TournamentStandingsTable
                                    pool={activePool}
                                    ratings={MHR_RATINGS_2026_PW_MAJOR}
                                    isJetsPool={activePool.poolName === 'E' || activePool.standings.some(s => s.teamName.toLowerCase().includes('jets'))}
                                />
                            ) : null}
                        </Box>
                    )}

                    {/* Tournament Format & Advancement Breakdown Card */}
                    <Paper
                        variant="outlined"
                        elevation={0}
                        sx={{
                            mb: { xs: 2.5, sm: 4 },
                            p: { xs: 1.5, sm: 3 },
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            borderColor: 'divider',
                            borderLeftWidth: 6,
                            borderLeftColor: 'secondary.main'
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                            <GavelIcon color="secondary" />
                            <Typography variant="h6" component="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                                How Advancement & Playoffs Work (32-Team 8-Pool Format)
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            The 2014 Jetspeed division features 32 teams divided into 8 four-team pools (Pools A through H). Chicago Jets compete in <strong>Pool E</strong> alongside California Wave 12AA, Traverse City North Stars, and Winnetka Warriors 2014 CS. Here is how seeding and progression work across the tournament:
                        </Typography>

                        <Stack spacing={{ xs: 1.5, sm: 2 }}>
                            {/* Phase 1 */}
                            <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 0.5 }}>
                                    <Chip label="Phase 1" size="small" color="primary" sx={{ fontWeight: 'bold', width: 'fit-content' }} />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Pool Round Robin (Friday – Saturday Morning • 3 Preliminary Games)
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    • Each team plays <strong>3 preliminary games</strong> against the other teams in their pool.<br />
                                    • Scoring: <strong>3 pts</strong> for a regulation win, <strong>2 pts</strong> for a shootout win, <strong>1 pt</strong> for a shootout loss, and <strong>0 pts</strong> for a regulation loss.<br />
                                    • <strong>Advancement (Top 2 to Playoffs):</strong> The <strong>top 2 teams from each pool (16 teams total)</strong> advance to Saturday afternoon's <strong>Sweet 16 (Round of 16) Playoff bracket</strong>. 3rd and 4th place teams advance to Saturday afternoon Consolation crossovers.<br />
                                    • Maximum <strong>7-goal differential</strong> applies to all preliminary games for tiebreaker calculations.
                                </Typography>
                            </Paper>

                            {/* Phase 2 */}
                            <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 0.5 }}>
                                    <Chip label="Phase 2" size="small" color="secondary" sx={{ fontWeight: 'bold', width: 'fit-content' }} />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Saturday Afternoon Crossovers: Sweet 16 & Consolation (01:55 PM – 02:50 PM CDT • Guaranteed 4th Game)
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    • <strong>Guaranteed 4th Game:</strong> Every single team plays a 4th game on Saturday afternoon within a tight 55-minute window (1:55 PM – 2:50 PM CDT).<br />
                                    • <strong>Sweet 16 Championship Playoff (Top 2 Pool Finishers):</strong><br />
                                    &nbsp;&nbsp;• <strong>1st in Pool E (E1st):</strong> Plays <strong>2nd in Pool D (D2nd)</strong> on Sat Nov 7 @ <strong>2:50 PM CDT</strong> at <strong>Orbit Ice Arena</strong> (Game #52).<br />
                                    &nbsp;&nbsp;• <strong>2nd in Pool E (E2nd):</strong> Plays <strong>1st in Pool D (D1st)</strong> on Sat Nov 7 @ <strong>2:10 PM CDT</strong> at <strong>Rosemont Ice Arena (East Rink)</strong> (Game #51).<br />
                                    &nbsp;&nbsp;• <em>Winners of these games advance to Sunday morning's Championship Quarterfinals (Elite 8).</em><br />
                                    • <strong>Consolation Crossovers (3rd & 4th Place Pool Finishers):</strong><br />
                                    &nbsp;&nbsp;• <strong>3rd in Pool E (E3rd):</strong> Plays <strong>3rd in Pool D (D3rd)</strong> on Sat Nov 7 @ <strong>1:55 PM CDT</strong> at <strong>Mount Prospect Ice Arena (South Rink)</strong> (Game #49).<br />
                                    &nbsp;&nbsp;• <strong>4th in Pool E (E4th):</strong> Plays <strong>4th in Pool D (D4th)</strong> on Sat Nov 7 @ <strong>1:55 PM CDT</strong> at <strong>Elk Grove Ice Arena (East Rink)</strong> (Game #50).
                                </Typography>
                            </Paper>

                            {/* Phase 3 */}
                            <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 0.5 }}>
                                    <Chip label="Phase 3" size="small" color="success" sx={{ fontWeight: 'bold', width: 'fit-content' }} />
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        Sunday Championship Bracket (Quarterfinals, Semifinals & Final)
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    • <strong>Quarterfinals (Elite 8):</strong> Sunday morning (<strong>07:10 AM – 08:20 AM CDT</strong>) at <strong>Elk Grove Ice Arena (East Rink)</strong>. The 8 winners of Saturday afternoon's Sweet 16 games compete here. (E1st winner plays Sun 7:10 AM; E2nd winner plays Sun 8:20 AM).<br />
                                    • <strong>Semifinals (Frozen 4):</strong> Sunday late morning (<strong>10:20 AM – 11:30 AM CDT</strong>) at <strong>North Shore Ice Arena</strong>.<br />
                                    • <strong>Championship Final:</strong> Sunday afternoon (<strong>02:45 PM CDT</strong>) at <strong>North Shore Ice Arena</strong> to crown the 2026 CCM World Invite Chicago 2014 Jetspeed Champion!
                                </Typography>
                            </Paper>
                        </Stack>
                    </Paper>

                    {/* Tiebreaker Rules Summary Accordion */}
                    <Accordion variant="outlined" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: 2, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <GavelIcon color="primary" />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Official Tiebreaker Rules & Tournament Guidelines
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Divider sx={{ mb: 2 }} />

                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                🏆 Point System:
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2, mt: 0.5 }}>
                                <li><Typography variant="body2"><strong>3 Points:</strong> Regulation Win</Typography></li>
                                <li><Typography variant="body2"><strong>2 Points:</strong> Shootout Win</Typography></li>
                                <li><Typography variant="body2"><strong>1 Point:</strong> Shootout Loss</Typography></li>
                                <li><Typography variant="body2"><strong>0 Points:</strong> Regulation Loss</Typography></li>
                                <li><Typography variant="body2"><em>Any forfeited game is recorded as a 1-0 score.</em></Typography></li>
                            </Box>

                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                🎯 Overtime & Shootout Rules:
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 2, mt: 0.5 }}>
                                <li>
                                    <Typography variant="body2">
                                        <strong>Preliminary Games:</strong> No overtime. If tied at the end of regulation, immediately proceeds to a 3-player shootout, followed by sudden-death shootout if still tied. (Every player on the bench must shoot once before any player shoots a second time).
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        <strong>Playoff / Elimination Games:</strong> Goaltenders switch ends; one 3-minute period of 3-on-3 sudden death; followed by 3-player shootout and sudden death shootout if necessary.
                                    </Typography>
                                </li>
                            </Box>

                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                📊 Seeding Tiebreakers (Within Pool & Overall):
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                <em>Maximum 7-goal differential applies to all preliminary games. At any point in the tiebreaker process, if one tied team defeated all other tied teams in preliminary play, that team is seeded first among the tied teams. Conversely, if one tied team lost to all other tied teams, that team is seeded last.</em>
                            </Typography>
                            <Box component="ol" sx={{ pl: 3, mb: 2, mt: 0.5 }}>
                                <li><Typography variant="body2"><strong>Head-to-Head:</strong> Outcome of game between tied teams.</Typography></li>
                                <li>
                                    <Typography variant="body2">
                                        <strong>Goal Quotient:</strong> Goals For / (Goals For + Goals Against). <em>(Higher percentage wins; max 7-goal diff per game).</em>
                                    </Typography>
                                </li>
                                <li><Typography variant="body2"><strong>Fewest Goals Against:</strong> Total goals allowed across preliminary games.</Typography></li>
                                <li><Typography variant="body2"><strong>Fastest Goal:</strong> Earliest goal scored in the tournament.</Typography></li>
                                <li><Typography variant="body2"><strong>Coin Flip:</strong> Final determination if all tiebreakers remain equal.</Typography></li>
                            </Box>

                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                ⏱️ Period Lengths & Game Protocol:
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 1, mt: 0.5 }}>
                                <li><Typography variant="body2"><strong>12U Division:</strong> Three 14-minute stop-time periods.</Typography></li>
                                <li><Typography variant="body2"><strong>Penalties:</strong> 1:30 Minor, 4:00 Major, 8:00 Misconduct.</Typography></li>
                                <li>
                                    <Typography variant="body2">
                                        <strong>Running Clock Rule:</strong> If a 7-goal lead exists at any point during the 2nd period (stops again if cut to 4), or a 5-goal lead exists during the 3rd period (stops again if cut to 2), the clock will run continuously.
                                    </Typography>
                                </li>
                                <li><Typography variant="body2"><strong>Timeouts:</strong> No timeouts in preliminary games. One 60-second timeout per team in playoff / elimination games.</Typography></li>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}

            {/* Direct Tournament Links Table */}
            <LinksTable title="Key Tournament Links" links={ccmWorldInviteLinks2026} />
        </>
    );
};

export default CcmWorldInvite2026Page;
